import { searchLots, type LotFilters, type SearchableLot } from './lot-search.js';

/**
 * Which lots the chosen layout is given, once search and the filter rail have
 * had their say.
 *
 * 🔴 EXTRACTED SO THE PAGE CANNOT QUIETLY NARROW THE WRONG THING. Browse and
 * Live became one page on 2026-09-14, which put a filter rail on the screen
 * that draws a bidder's standing. Two sets now exist where one did before:
 *
 *   `openLots`  — every lot still open. The standing counts and the sale's lot
 *                 count read THIS. A bidder who narrows to "no reserve, under
 *                 $50" has not stopped winning the lots outside that filter,
 *                 and a panel that said otherwise would be lying about money.
 *   the result  — the narrowed set. ONLY the layouts read this.
 *
 * Keeping the rule here, pure and tested, is what stops a later edit swapping
 * one for the other in a JSX diff that looks like a rename.
 *
 * The filtering itself is `searchLots` — the phone's filter sheet has used it
 * since P2.4. A second implementation would be how "no reserve" comes to mean
 * two different things on two surfaces.
 *
 * 🔴 MOVED HERE FROM `apps/web` ON 2026-09-21, when the PHONE merged Browse
 * into its Live Room and needed the identical rule. It sat in the web page's
 * own directory for the week the website was the only merged surface, which
 * was fine right up until it wasn't: a second copy under `apps/bidder` is
 * exactly the mechanical divergence this package exists to prevent, and the
 * two sets it distinguishes — every open lot vs. the narrowed view — are about
 * MONEY, which is the worst place for two surfaces to disagree.
 */
export function narrowRoom<R extends SearchableLot, L extends { id: string; secs: number }>(
  rows: readonly R[],
  openLots: readonly L[],
  query: string,
  filters: LotFilters,
  nowMs: number,
): L[] {
  const matched = searchLots(rows, query, filters, nowMs);
  const rank = new Map(matched.map((r, i) => [r.id, i]));
  const out = openLots.filter((l) => rank.has(l.id));

  /**
   * 🔴 `closing_soonest` IS RE-DERIVED FROM `secs`, NOT TAKEN FROM `searchLots`.
   *
   * The two agree for every lot that has an `ends_at`. They diverge for a lot
   * that only has `scheduled_ends_at`: `searchLots` sorts on `ends_at` alone
   * and would drop such a lot to the end, while `secs` was computed from
   * `ends_at ?? scheduled_ends_at` and places it correctly. That IS the run
   * order the room has always drawn, and the run order is the one ordering the
   * layouts assume — getting it wrong reorders a live sale.
   *
   * Every other sort is the bidder's explicit choice and passes through in
   * exactly the order the shared function produced.
   */
  out.sort((a, b) =>
    filters.sort === 'closing_soonest'
      ? a.secs - b.secs
      : (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0),
  );
  return out;
}
